import {getCurrentInstance, onBeforeUnmount, onMounted, ref} from 'vue';
import {useRequestEvent, useRequestHeaders} from '#imports';

export type RouteResolversFn = {
    startWith: (value: string) => boolean;
    matchRegXp: (value: RegExp) => boolean;
    exact: (value: string) => boolean;
};

/**
 * Composable that will provide tools to observe the current url pathname
 */
export function useLocationWatcher() {
    const headers = useRequestHeaders(['host', 'referer']);
    const event = useRequestEvent();

    const origin = ref<string>(import.meta.client ? window.location.origin : (headers.host ?? ''));
    const pathname = ref<string>(import.meta.client ? window.location.pathname : (event?.node.req as any).originalUrl);

    let locationObserver: MutationObserver;

    function onLocationChange() {
        if (pathname.value !== document.location.pathname) {
            origin.value = window.location.origin;
            pathname.value = window.location.pathname;
        }
    }

    /** Hack to observe window location change, no native event in JS */
    if (getCurrentInstance()) {
        onMounted(() => {
            const root = document.querySelector('body')!;

            locationObserver = new MutationObserver((mutations) => {
                mutations.forEach(onLocationChange);
            });

            locationObserver.observe(root, {
                childList: true,
                subtree: true,
            });
        });

        onBeforeUnmount(() => {
            locationObserver?.disconnect();
        });
    }

    // Normalize an URL from a pathname to avoid errors with compares
    function buildFormatedUrl(pathname: string): string {
        return new URL(pathname, 'http://malt.com').pathname;
    }

    function exact(value: string): boolean {
        const inputPathName = buildFormatedUrl(value);
        return buildFormatedUrl(pathname.value) === inputPathName;
    }

    function startWith(value: string): boolean {
        const inputPathName = buildFormatedUrl(value);
        return buildFormatedUrl(pathname.value).startsWith(inputPathName);
    }

    function matchRegXp(value: RegExp): boolean {
        return value.test(buildFormatedUrl(pathname.value));
    }

    return {
        origin,
        pathname,
        routeUtils: {exact, startWith, matchRegXp},
    };
}
